import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
// Phase 25 -- env-driven Sentry init (strict no-op when REACT_APP_SENTRY_DSN unset).
import { initSentry } from "@/lib/sentry";
// D48-d -- PWA "new version available" prompt. Idempotent boot; safe
// to init before the SW is registered because it polls for the
// registration internally.
import { initSwUpdatePrompt } from "@/lib/swUpdatePrompt";
initSentry();
initSwUpdatePrompt();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

/* D19.1 P0-C -- `--vh` polyfill for iOS Safari address-bar height quirk.
 *
 * `100vh` on iOS Safari includes the URL bar so layout jumps 60-100px
 * whenever the bar retracts/expands. Modern browsers have `100dvh`
 * (dynamic viewport height) which we prefer everywhere; this custom
 * property is the fallback for browsers older than iOS 15.4.
 *
 * Wired once at boot + on `resize` / `orientationchange`. Cheap:
 * ~1ms per event and only writes when the value actually changed. */
(function installViewportHeightFallback() {
  if (typeof window === "undefined") return;
  const setVh = () => {
    const next = `${window.innerHeight * 0.01}px`;
    if (document.documentElement.style.getPropertyValue("--vh") !== next) {
      document.documentElement.style.setProperty("--vh", next);
    }
  };
  setVh();
  window.addEventListener("resize", setVh, { passive: true });
  window.addEventListener("orientationchange", setVh, { passive: true });
})();



/* Phase 14A -- deterministic Service Worker registration with strict
 * version pinning. On boot:
 *   1. Fetch `/sw-version.json` (with cache-buster) to learn the desired
 *      version.
 *   2. If a live SW exists, message it and read back its embedded version.
 *   3. If versions differ (or no SW), unregister + purge caches and
 *      re-register the fresh sw.js. This prevents the "stale bundle disaster"
 *      where returning clients pin outdated JS forever.
 * The `sw.js?v=X` query string forces the browser to fetch the new script
 * on every version bump.
 */
if ("serviceWorker" in navigator) {
  window.addEventListener("load", async () => {
    try {
      const resp = await fetch(`/sw-version.json?ts=${Date.now()}`, { cache: "no-store" });
      const desired = resp.ok ? (await resp.json()).version : null;
      if (!desired) return; // nothing to register against

      const existing = await navigator.serviceWorker.getRegistrations();
      // Ask each existing SW its version. If any mismatch, unregister them all.
      let mismatch = false;
      for (const reg of existing) {
        const controller = reg.active || reg.installing || reg.waiting;
        if (!controller) { mismatch = true; continue; }
        const gotVersion = await new Promise((resolve) => {
          const timer = setTimeout(() => resolve(null), 500);
          const ch = new MessageChannel();
          ch.port1.onmessage = (ev) => {
            clearTimeout(timer);
            resolve(ev.data && ev.data.version);
          };
          try {
            controller.postMessage({ type: "sw:version?" }, [ch.port2]);
          } catch (_) {
            clearTimeout(timer);
            resolve(null);
          }
        });
        if (gotVersion !== desired) { mismatch = true; }
      }
      if (mismatch && existing.length) {
        await Promise.all(existing.map((r) => r.unregister().catch(() => null)));
        if (window.caches?.keys) {
          const keys = await window.caches.keys();
          await Promise.all(keys.map((k) => window.caches.delete(k).catch(() => null)));
        }
      }
      // Register the fresh SW with a version query so browsers do not serve
      // a cached script.
      await navigator.serviceWorker.register(`/sw.js?v=${encodeURIComponent(desired)}`);
    } catch (_) {
      /* Non-fatal -- push simply won't work this session. */
    }
  });
}
